import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FolderOpen } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export const MRD_REQUESTS_RETURN_TO = '/mrd?tab=requests';

export function LogRequestPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), MRD_REQUESTS_RETURN_TO);
  const [patientId, setPatientId] = useState('');
  const [requestedBy, setRequestedBy] = useState('');
  const [requesterType, setRequesterType] = useState('patient');
  const [purpose, setPurpose] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => post('/mrd/requests', { patientId, requestedBy, requesterType, purpose }),
    onSuccess: () => {
      toast.success('Request logged');
      void queryClient.invalidateQueries({ queryKey: ['record-requests'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(patientId) && requestedBy.trim().length >= 2 && purpose.trim().length >= 3;
  const dirty = !create.isSuccess && (Boolean(patientId) || requestedBy !== '' || purpose !== '' || requesterType !== 'patient');

  return (
    <FormScreen
      title="Log a record request"
      icon={FolderOpen}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('admin', 'request')} icon={FolderOpen}>
        <Field label="Patient" required className="sm:col-span-2">
          <PatientPicker value={patientId} onChange={setPatientId} />
        </Field>
        <Field label="Requested by" required>
          <Input value={requestedBy} onChange={(e) => setRequestedBy(e.target.value)} />
        </Field>
        <Field label="Acting as" required>
          <ChoiceButtons
            layout="chips"
            value={requesterType}
            onChange={setRequesterType}
            required
            options={['patient', 'relative', 'legal', 'insurer', 'internal'].map((t) => ({ value: t, label: humanize(t) }))}
          />
        </Field>
        <Field label="Purpose" required className="sm:col-span-2">
          <Textarea rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Log" loading={create.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
